// app/(main)/_layout.tsx
import HomeIcon from "@/assets/images/icons/home_default.svg";
import NavIconBookmark from "@/assets/images/icons/nav-icon-bookmark.svg";
import NavIconLiveSession from "@/assets/images/icons/nav-icon-live-session.svg";
import NavIconSearch from "@/assets/images/icons/nav-icon-search.svg";
import { AppLoadingScreen } from "@/components/AppLoadingScreen";
import { GetAppPopup } from "@/components/GetAppPopup";
import { MobileDrawer } from "@/components/MobileDrawer";
import { OfflineBanner } from "@/components/OfflineBanner";
import { ProPromoBanner } from "@/components/ProPromoBanner";
import { SSOLoadingScreen } from "@/components/SSOLoadingScreen";
import { SummerSaleBanner } from "@/components/SummerSaleBanner";
import { ErrorDisplay } from "@/components/ui";
import { useLoadingTracker } from "@/hooks/useLoadingTracker";
import { usePageViewTracking } from "@/hooks/usePageViewTracking";
import { useSSOTokenExchange } from "@/hooks/useSSOTokenExchange";
import { useWordPressLoginRedirect } from "@/hooks/useWordPressLoginRedirect";
import { isAnonAllowedPath } from "@/lib/anonAccess";
import { URLS } from "@/lib/constants";
import { useOfflineBootstrap } from "@/lib/offline/bootstrap";
import { useIsOnline } from "@/lib/offline/network";
import { CommunityWebViewProvider } from "@/lib/CommunityWebViewContext";
import { DrawerProvider, useDrawer } from "@/lib/DrawerContext";
import type { AppLoadingStage } from "@/lib/loadingMessages";
import { goHome } from "@/lib/navigation";
import { usePlayerFocusActive } from "@/lib/playerFocusStore";
import { useIsLandscapeMobile, useIsTabletOrWeb } from "@/lib/useDeviceType";
import { redirectToWordPressLogin } from "@/lib/wordpress-redirect";
import { Ionicons } from "@expo/vector-icons";
import { Redirect, Stack, Tabs, usePathname } from "expo-router";
import { Linking, Platform, Pressable, View } from "react-native";
import { SafeAreaInsetsContext, useSafeAreaInsets } from "react-native-safe-area-context";
import { useAuth } from "../../lib/AuthContext";

const ACTIVE = "#E4572E";
const INACTIVE = "#B3B3B3";
const BAR_BG = "#000";

function Icon(name: keyof typeof Ionicons.glyphMap) {
  const NamedIcon = ({ color }: { color: string }) => (
    <Ionicons name={name} size={22} color={color} />
  );
  NamedIcon.displayName = `Icon(${name})`;
  return NamedIcon;
}

// Single bottom-bar button — accepts a render fn so we can mix Ionicons + SVGs.
function BottomBarButton({
  active,
  onPress,
  renderIcon,
}: {
  active: boolean;
  onPress: () => void;
  renderIcon: (color: string) => React.ReactNode;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        paddingVertical: 16,
      }}
    >
      {renderIcon(active ? ACTIVE : INACTIVE)}
    </Pressable>
  );
}

function MainLayoutContent() {
  const { user, loading, membershipTier, membershipLoading } = useAuth();
  const isTabletOrWeb = useIsTabletOrWeb();
  const pathname = usePathname();
  const isLandscape = useIsLandscapeMobile();
  const playerFocusActive = usePlayerFocusActive();
  const isVideoScreen = /lesson-slug|\/courses\/[^\/]+\/[^\/]+$/.test(pathname);
  const tabBarDisplay =
    (isLandscape && isVideoScreen) || playerFocusActive ? "none" : "flex";
  const { ssoProcessing, ssoToken, ssoError } = useSSOTokenExchange();
  const {
    isOpen: drawerOpen,
    openSearch,
    searchOpen,
    openLiveSessions,
    liveSessionsOpen,
    openBookmarks,
    bookmarksOpen,
    closeAllOverlays,
  } = useDrawer();

  useWordPressLoginRedirect();
  usePageViewTracking();
  // Reconnects background downloads and replays offline progress once signed in.
  useOfflineBootstrap(user?.id);
  // The offline banner already pads for the status bar, so while it shows,
  // the navigators below must not add the top inset again (black gap).
  const online = useIsOnline();
  const insets = useSafeAreaInsets();
  // Hidden while the player owns the screen (landscape / focus mode) so the
  // video gets the full height it was measured for.
  const offlineBannerVisible = Platform.OS !== "web" && !online && !playerFocusActive;
  const insetsBelowBanner = offlineBannerVisible ? { ...insets, top: 0 } : insets;

  const onAnonAllowedRoute = isAnonAllowedPath(pathname);
  const isAnonUser = !!user?.is_anonymous;
  // Anon users only "count" as logged in on the routes they're allowed to see.
  // Everywhere else they should be treated like an unauthenticated visitor
  // (useWordPressLoginRedirect handles the actual signout+bounce).
  const hasEffectiveUser = !!user && !(isAnonUser && !onAnonAllowedRoute);

  const loadingStage: AppLoadingStage | null =
    loading ? 'auth:loading'
    : ssoProcessing ? 'sso:processing'
    : !hasEffectiveUser && ssoToken ? 'auth:waiting_sso'
    : !hasEffectiveUser && !ssoError && !onAnonAllowedRoute ? 'auth:redirecting'
    : null;

  useLoadingTracker({
    isLoading: loadingStage !== null,
    stage: loadingStage,
    hasSsoToken: !!ssoToken,
    resolvedAsError: !!ssoError,
  });

  if (loading) return <AppLoadingScreen stage="auth:loading" />;
  if (ssoProcessing) return <SSOLoadingScreen />;

  if (ssoError) {
    return (
      <ErrorDisplay
        error={ssoError}
        title="Login failed"
        message="There was a problem signing you in. Please try again."
        onRetry={() => redirectToWordPressLogin()}
        retryText="Try logging in again"
      />
    );
  }

  if (!hasEffectiveUser) {
    if (Platform.OS === "web") {
      // On anon-allowed routes, AuthContext is creating an anonymous session;
      // show the same lightweight loading screen as we do for ongoing auth work
      // rather than the "redirecting to WordPress" splash.
      if (onAnonAllowedRoute) {
        return <AppLoadingScreen stage="auth:loading" />;
      }
      return <AppLoadingScreen stage={ssoToken ? "auth:waiting_sso" : "auth:redirecting"} />;
    }
    return <Redirect href="/auth/login" />;
  }

  // Web keeps the top header; native "tablet" uses header; all phones (any orientation) use Tabs.
  if (Platform.OS === "web" || isTabletOrWeb) {
    return (
      <View style={{ flex: 1 }}>
        {offlineBannerVisible ? <OfflineBanner /> : null}
        <SafeAreaInsetsContext.Provider value={insetsBelowBanner}>
        <SummerSaleBanner />
        <ProPromoBanner />
        <Stack
          screenOptions={{
            headerShown: false,
          }}
        >
          <Stack.Screen name="(home)" />
          <Stack.Screen name="purchased-courses" />
          <Stack.Screen name="bookmarks" />
          <Stack.Screen name="explore" />
          <Stack.Screen name="live" />
          <Stack.Screen name="library" />
          <Stack.Screen name="upgrade" />
          <Stack.Screen name="membership" />
          <Stack.Screen name="settings" />
          <Stack.Screen name="downloads" />
          <Stack.Screen name="welcome" />
        </Stack>
        </SafeAreaInsetsContext.Provider>
        <GetAppPopup />
      </View>
    );
  }

  // Phones (portrait + landscape): always show bottom tabs
  return (
    <View style={{ flex: 1 }}>
      {offlineBannerVisible ? <OfflineBanner /> : null}
      <SafeAreaInsetsContext.Provider value={insetsBelowBanner}>
      <SummerSaleBanner />
      <ProPromoBanner />
      <Tabs
      screenOptions={{
        headerShown: false,
        tabBarActiveTintColor: ACTIVE,
        tabBarInactiveTintColor: INACTIVE,
        tabBarShowLabel: true,
        tabBarLabelStyle: {
          marginTop: 6,
          fontSize: 11,
          letterSpacing: 1,
          fontWeight: "700",
        },
        tabBarStyle: {
          display: tabBarDisplay,
          backgroundColor: BAR_BG,
          borderTopWidth: 0,
        },
        tabBarItemStyle: { justifyContent: "center", alignItems: "center" },
      }}
      tabBar={(props) => {
        const { navigation, state } = props;
        const focusedName = state.routes[state.index]?.name;
        const onBookmarksScreen = focusedName === "bookmarks";
        const coursesKey = state.routes.find((r) => r.name === "(home)")?.key;
        const noOverlay =
          !searchOpen && !liveSessionsOpen && !bookmarksOpen && !drawerOpen;
        const homeActive = noOverlay && !onBookmarksScreen;
        const bookmarkActive = bookmarksOpen || onBookmarksScreen;
        return (
          <View
            style={{
              flexDirection: "row",
              backgroundColor: BAR_BG,
              display: tabBarDisplay,
            }}
          >
            <BottomBarButton
              active={homeActive}
              onPress={() => {
                closeAllOverlays();
                navigation.navigate("(home)" as never);
                // Then popToTop the home stack back to the courses catalog — the
                // standard "tap the active tab" behaviour. Since instructors now
                // lives in this same stack, this also covers returning from it.
                goHome();
                // tabPress is the same signal React Navigation's default tab bar
                // emits; useScrollToTop() on the focused courses screen listens
                // for it to scroll the list back to the top.
                if (coursesKey) {
                  navigation.emit({
                    type: "tabPress",
                    target: coursesKey,
                    canPreventDefault: true,
                  });
                }
              }}
              renderIcon={(c) => <HomeIcon width={24} height={24} color={c} />}
            />
            <BottomBarButton
              active={searchOpen}
              onPress={openSearch}
              renderIcon={(c) => (
                <NavIconSearch width={36} height={36} color={c} />
              )}
            />
            {/* Non-subscribed users don't get Live Sessions in the bar at all
                (kept visible while the tier loads to avoid a layout jump). */}
            {(membershipLoading || membershipTier !== "non_member") && (
              <BottomBarButton
                active={liveSessionsOpen}
                onPress={openLiveSessions}
                renderIcon={(c) => (
                  <NavIconLiveSession width={36} height={36} color={c} />
                )}
              />
            )}
            <BottomBarButton
              active={bookmarkActive}
              onPress={() => {
                // Already on manage screen — don't open the quick sheet on top
                if (onBookmarksScreen) return;
                openBookmarks();
              }}
              renderIcon={(c) => (
                <NavIconBookmark width={36} height={36} color={c} />
              )}
            />
          </View>
        );
      }}
    >
      <Tabs.Screen
        name="(home)"
        options={{ title: "COURSES", tabBarIcon: Icon("videocam-outline") }}
      />
      <Tabs.Screen name="bookmarks" options={{ href: null }} />
      <Tabs.Screen name="explore" options={{ href: null }} />
      <Tabs.Screen name="live" options={{ href: null }} />
      <Tabs.Screen name="library" options={{ href: null }} />
      <Tabs.Screen name="upgrade" options={{ href: null }} />
      <Tabs.Screen name="membership" options={{ href: null }} />
      <Tabs.Screen name="settings" options={{ href: null }} />
      <Tabs.Screen name="downloads" options={{ href: null }} />
      <Tabs.Screen name="welcome" options={{ href: null }} />
    </Tabs>
      </SafeAreaInsetsContext.Provider>
    </View>
  );
}

export default function MainLayout() {
  return (
    <DrawerProvider>
      <CommunityWebViewProvider>
        <MobileDrawer>
          <MainLayoutContent />
        </MobileDrawer>
      </CommunityWebViewProvider>
    </DrawerProvider>
  );
}
